// src/pages/Login.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { loginUser } from "../services/api";  // Only import loginUser
import { ROUTES } from "../config";
import ResendRegistrationLinkBanner from "../components/ResendRegistrationLinkBanner";
import "../styles/Login.css";

/**
 * Login page for all user roles.
 *
 * Handles authentication and error feedback.
 * Role-based redirection is now handled globally in App.js.
 *
 * @component
 * @returns {JSX.Element}
 */
const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setErrorCode(null);
    setSubmitting(true);

    try {
      await loginUser(identifier, password);

      // loginUser already stores:
      // - token
      // - refreshToken
      // - role
      // - schoolName

      // App.js will detect the stored role and redirect automatically
      // So we just navigate to home — redirect logic will kick in
      navigate(ROUTES.HOME, { replace: true });
    } catch (err) {
      let msg = "Invalid email or password. Please try again.";
      let code = null;

      // Handle various error shapes from Axios/FastAPI
      const detail = err?.response?.data?.detail;
      if (detail) {
        if (typeof detail === "string") {
          msg = detail;
        } else if (detail.message) {
          msg = detail.message;
          code = detail.error_code || null;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors (e.g. missing fields)
          msg = detail.map((d) => d.msg).join("; ") || msg;
        }
      } else if (err?.message) {
        msg = err.message;
      }

      setErrorMessage(msg);
      setErrorCode(code);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container page">
      <div className="login-card">
        <header className="login-header">
          <h1 className="login-title">Login</h1>
          <p className="login-subtitle">
            Sign in with your GhrianGhraffy account to manage and share your school&apos;s photos.
          </p>
        </header>

        {errorMessage && (
          <p className="error-message" aria-live="polite">
            {errorMessage}
          </p>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="login-field-group">
            <label htmlFor="login-email" className="login-label">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              className="login-input"
              placeholder="you@school.ie"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              disabled={submitting}
              autoComplete="username"
            />
          </div>

          <div className="login-field-group">
            <label htmlFor="login-password" className="login-label">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              className="login-input"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="login-btn" disabled={submitting}>
            {submitting ? "Signing in…" : "Login"}
          </button>
        </form>

        <p className="login-links">
          <Link to={ROUTES.FORGOT_PASSWORD} className="template-download-link login-forgot-link">
            Forgot your password?
          </Link>
        </p>

        {errorCode === "INCOMPLETE_SETUP" && identifier && (
          <div className="login-resend-banner-wrap">
            <ResendRegistrationLinkBanner email={identifier} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;